import { useSession } from 'next-auth/react'

import useBrazeIntegration from '@context/useBrazeIntegration'
import { useForm } from 'react-hook-form'

import profileTextNodes from '@data/i18n/profile.json'

import { lang } from '@lib/constants'
import useCustomer from '@lib/hooks/useCustomer'
import { updateCustomer } from '@lib/operations/customer'
import type { Customer } from '@lib/types/customer'
import type { FormAddress } from '@lib/types/profile-pages'

import EmailInput from '@components/form-controls/EmailInput'
import PhoneNumberInput, { maskPhoneNumber } from '@components/form-controls/PhoneNumberInput'
import TextInput from '@components/form-controls/TextInput'

type Props = {
	customer: Customer
	subscriptionAddress?: FormAddress
}

export default function PersonalInformationForms({ customer, subscriptionAddress }: Props) {
	const session = useSession()
	const { revalidate: revalidateCustomerData } = useCustomer()

	const { brazeFunction } = useBrazeIntegration()

	// Personal information form
	const {
		register,
		handleSubmit,
		setError,
		watch,
		formState: { errors, isSubmitting, isDirty }
	} = useForm<Customer>({
		defaultValues: {
			...(subscriptionAddress || {}),
			...customer,
			...(customer.phone ? { phone: maskPhoneNumber(customer.phone) } : {})
		}
	})

	// Watch email input to style when has value
	const firstNameInput = watch('firstName')
	const lastNameInput = watch('lastName')
	const emailInput = watch('email')

	const submitPersonalInfo = async (data: Customer) => {
		const customerAccessToken = (session.data as any)?.shopifyAccessToken as string
		const newPersonalInformation = {
			firstName: data.firstName,
			lastName: data.lastName,
			phone: data.phone && lang === 'en-us' ? `+1${data.phone.replace(/\D/g, '')}` : data.phone
		} as Partial<Customer>

		const updatePersonalInfo = await updateCustomer(customerAccessToken, newPersonalInformation)

		if (updatePersonalInfo !== true) {
			// TODO: handle errors in the UI
			for (const error of updatePersonalInfo) {
				const errorField = error.field[1] as keyof Customer
				setError(errorField, { message: error.message })
			}
		}

		if (updatePersonalInfo) {
			const braze = await brazeFunction()

			if (braze) {
				braze.getUser()?.setFirstName(data.firstName)
				braze.getUser()?.setLastName(data.lastName)
				if (data.phone) braze.getUser()?.setPhoneNumber(data.phone)
			}

			revalidateCustomerData()
		}
	}

	return (
		<form
			className="flex flex-col gap-y-12"
			onSubmit={handleSubmit(async (data) => submitPersonalInfo(data))}
		>
			{/* Personal information */}
			<fieldset className="flex flex-col space-y-4">
				<legend className="font-medium">
					{profileTextNodes.account.personalInformationForms.personalInformationTitle}
				</legend>

				{/* Email */}
				<EmailInput
					register={register}
					errors={errors?.email?.message}
					isFilled={!!emailInput}
					disabled
					defaultValue={customer?.email}
					placeholder={profileTextNodes.account.personalInformationForms.email}
				/>

				{/* First name */}
				<TextInput
					register={register}
					id="firstName"
					placeholder={profileTextNodes.account.personalInformationForms.firstName}
					isFilled={!!firstNameInput}
					autoComplete="given-name"
				/>

				{/* Last name */}
				<TextInput
					register={register}
					id="lastName"
					placeholder={profileTextNodes.account.personalInformationForms.lastName}
					isFilled={!!lastNameInput}
					autoComplete="family-name"
				/>

				{/* Phone number */}
				<PhoneNumberInput
					register={register}
					errors={errors?.phone?.message}
					id="phone"
					placeholder={profileTextNodes.account.personalInformationForms.phoneNumber}
					required={false}
				/>
			</fieldset>

			{/* Submit  */}
			<button className="button button-primary" type="submit" disabled={isSubmitting || !isDirty}>
				{isSubmitting
					? profileTextNodes.account.personalInformationForms.PersonalInfoCTALabelSubmitted
					: profileTextNodes.account.personalInformationForms.PersonalInfoCTALabel}
			</button>
		</form>
	)
}
